import {
  PlanAction,
  BillingFrequency,
  BillableFeature,
  BillingStrategy,
  BillingVersion,
} from '@pncl/common-models';
import { BillableFeatureConfig, StepPricingConfig } from '../admin/types.interface';

/** Re-export for FE consumers that import billing types from this module. */
export { BillingVersion };

export interface Plan {
  addon_applicability: string;
  applicable_addons?: Array<any>;
  charge_model: string;
  currency_code: string;
  enabled_in_hosted_pages: boolean;
  enabled_in_portal: boolean;
  free_quantity: number;
  giftable: boolean;
  id: string;
  is_shippable: boolean;
  name: string;
  object: string;
  period: number;
  period_unit: string;
  price: number;
  pricing_model: string;
  status: string;
  taxable: boolean;
  description?: string;
}

export interface Addon {
  charge_type: string;
  currency_code: string;
  enabled_in_portal: boolean;
  id: string;
  is_shippable: boolean;
  name: string;
  object: string;
  period: number;
  period_unit: string;
  price: number;
  pricing_model: string;
  resource_version: number;
  show_description_in_invoices: boolean;
  show_description_in_quotes: boolean;
  status: string;
  taxable: boolean;
  type: string;
  unit: string;
  updated_at: number;
}

export interface BillingAddress {
  first_name: string;
  last_name: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  countryOb?: object;
}

export interface Customer {
  first_name: string;
  last_name: string;
  email: string;
}

export interface PlanSubscription {
  user_id: string;
  plan_id: string;
  currency_code: string;
  period_unit: string;
  auto_renewal: boolean;
  teacherQuantity: number;
  studentQuantity: number;
  billing_address: BillingAddress;
  customer: Customer;
  token_id: string;
  autofill: boolean;
}

export const PlanSubscriptionDefaults: PlanSubscription = {
  user_id: '',
  plan_id: '',
  currency_code: '',
  period_unit: '',
  auto_renewal: true,
  teacherQuantity: 1,
  studentQuantity: 0,
  billing_address: {
    first_name: '',
    last_name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
  customer: {
    first_name: '',
    last_name: '',
    email: '',
  },
  token_id: '',
  autofill: false,
};

export interface PostPayment {
  plan: string;
  amount: number;
  teacherQuantity: number;
  studentQuantity: number;
  currency_code: string;
  billing_period_unit: string;
  next_billing_at: number;
  maskedNumber: string;
  card_type: string;
  first_name: string;
  email: string;
  last_name: string;
  full_name: string;
  line1: string;
  line2: string;
  city: string;
  state_code: string;
  zip: string;
  country: string;
  unbilled_charges: number;
}

export interface Estimate {
  total: number;
  tax_amount: number;
  description: string;
  currency_code: string;
}

export interface Card {
  status: string;
  iin: string;
  last4: string;
  maskedCvv?: string;
  card_type: string;
  expiry: string;
  masked_number: string;
}

export interface CardDetails {
  first_name: string;
  last_name: string;
  billing_address: BillingAddress;
  card: Card;
}

export enum ChargebeeSubscriptionStatus {
  FUTURE = 'future',
  IN_TRIAL = 'in_trial',
  ACTIVE = 'active',
  NON_RENEWING = 'non_renewing',
  PAUSED = 'paused',
  CANCELLED = 'cancelled',
}

export enum SubscriptionStatus {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
}

export enum SUBSCRIPTION_TYPES {
  TRIAL = 'TRIAL',
  PAID = 'PAID',
  FREE = 'FREE',
  ENTERPRISE = 'ENTERPRISE',
}

export enum InstitutionTiers {
  Pro = 'Pro',
  Premium = 'Premium',
  Custom = 'Custom',
}

export enum SubscriptionEvent {
  subscription_updated_manually = 'subscription_updated_manually',
}

export interface BillingStatus {
  subscription_status?: SubscriptionStatus;
  subscription?: string | Subscription;
  customerId?: string;
  forceBillDate?: Date;
}

export interface Subscription {
  _id?: string;
  chargebeeId?: string;
  email?: string;
  plan?: string;
  nextBillingAt?: number;
}

export interface CheckoutInfo {
  checkout_url: string;
  hosted_page_id: string;
}

export interface PortalInfo {
  access_url: string;
  subscriptionId?: string;
}

export interface SubscriptionInfo {
  status: ChargebeeSubscriptionStatus;
}

export enum HubspotContactProperties {
  APP_LAST_VISIT = 'APP_LAST_VISIT',
  APP_PLANS_LAST_VISIT = 'APP_PLANS_LAST_VISIT ',
}

/* Subscription types that can use "pro" features */
export const enterpriseFeatureSubscriptionTypes = [SUBSCRIPTION_TYPES.ENTERPRISE];

export const proFeatureSubscriptionTypes = [
  SUBSCRIPTION_TYPES.ENTERPRISE,
  SUBSCRIPTION_TYPES.PAID,
  SUBSCRIPTION_TYPES.TRIAL,
];

export const allFeatureSubscriptionTypes = [
  SUBSCRIPTION_TYPES.ENTERPRISE,
  SUBSCRIPTION_TYPES.PAID,
  SUBSCRIPTION_TYPES.TRIAL,
  SUBSCRIPTION_TYPES.FREE,
];

export const ReadableBillableFeature: Record<BillableFeature, string> = {
  [BillableFeature.SpaceDuration]: 'Space Duration',
  [BillableFeature.CloudRecordingDuration]: 'Cloud Recording Duration',
  [BillableFeature.WebViewerDuration]: 'Web Viewer Duration',
};

export interface BillingReport {
  totalCharges: number;
  billingPeriodStartDate: string;
  billingPeriodEndDate: string;
  isBillingPeriodComplete: boolean;
  billableItems: BillableItem[];
  nextBillingDate: string;
  billingVersion?: BillingVersion;
  spaceDurationV2Breakdown?: SpaceDurationV2Breakdown;
}

export interface BillableItem {
  totalAvailableCredits: number;
  totalUsage: number; // in hours
  billableRate: number;
  billableCharges: number;
  feature: BillableFeature;
}

/** One cohort of sessions that shared the same pinned rate anchors. */
export interface V2SpaceDurationBillableGroup {
  groupSessionRate: number;
  additionalParticipantRate: number; // steps[0].pricePerUserHour
  groupSessionThreshold: number;
  totalCredits: number;
  totalGroupSessionCredits: number;
  totalAdditionalParticipantCredits: number;
  totalGroupSessionHours: number;
  totalAdditionalParticipantHours: number;
}

/**
 * UX / display breakdown for V2 SpaceDuration.
 * Period dollar rollups + per-config groups (hours/rates shown per group).
 */
export interface SpaceDurationV2Breakdown {
  billableGroups: V2SpaceDurationBillableGroup[];
  totalUsageDollars: number; // Σ billableGroups[].totalCredits
  allowanceDollars: number; // live stepConfig.credits
  billableChargesDollars: number; // max(0, totalUsageDollars - allowanceDollars)
}

export interface CustomerPlan {
  planId?: string;
  planName: string;
  billingFrequency: string;
}

export enum FeatureName {
  // Pricing
  MONTHLY_MINIMUM = 'MONTHLY_MINIMUM',
  HOURS_INCLUDED = 'HOURS_INCLUDED',

  // Per Session Cost
  FIRST_4_USERS = 'FIRST_4_USERS',
  EACH_USER_THEREAFTER = 'EACH_USER_THEREAFTER',

  // Live Teaching
  NUMBER_OF_SPACES = 'NUMBER_OF_SPACES',
  NUMBER_OF_PEOPLE_IN_SPACE = 'NUMBER_OF_PEOPLE_IN_SPACE',
  NUMBER_OF_BOARDS_IN_SPACE = 'NUMBER_OF_BOARDS_IN_SPACE',
  PERSISTENT_SPACE_LINKS = 'PERSISTENT_SPACE_LINKS',
  UPLOAD_PDFS_IMAGES = 'UPLOAD_PDFS_IMAGES',
  RESOURCE_LIBRARY = 'RESOURCE_LIBRARY',
  LEAD_LOCK_AND_FOLLOW = 'LEAD_LOCK_AND_FOLLOW',
  DISTRACTION_ALERTS = 'DISTRACTION_ALERTS',
  CHATS = 'CHATS',
  CELEBRATIONS_AND_REACTIONS = 'CELEBRATIONS_AND_REACTIONS',
  PARTICIPANT_MANAGEMENT = 'PARTICIPANT_MANAGEMENT',
  WAITING_ROOM = 'WAITING_ROOM',
  RAISE_HANDS = 'RAISE_HANDS',
  EXPORT_SPACES_AS_IMAGE = 'EXPORT_SPACES_AS_IMAGE',
  EXPORT_SPACES_AS_SPACE_FILE = 'EXPORT_SPACES_AS_SPACE_FILE',
  BREAKOUT_ROOMS = 'BREAKOUT_ROOMS',
  OBSERVER_MODE = 'OBSERVER_MODE',
  CHILD_MODE = 'CHILD_MODE',

  // Calling
  VIDEO_CALLING = 'VIDEO_CALLING',
  ADVANCED_NOISE_CANCELLATION = 'ADVANCED_NOISE_CANCELLATION',
  ANNOTATE_OVER_SCREENSHARE = 'ANNOTATE_OVER_SCREENSHARE',
  VIRTUAL_BACKGROUNDS = 'VIRTUAL_BACKGROUNDS',
  FOCUS_MODE = 'FOCUS_MODE',
  ENFORCE_VIRTUAL_BACKGROUNDS = 'ENFORCE_VIRTUAL_BACKGROUNDS',
  PHONE_DIAL_IN = 'PHONE_DIAL_IN',
  LIVESTREAMING = 'LIVESTREAMING',

  // Scheduling
  CREATE_SCHEDULED_EVENTS = 'CREATE_SCHEDULED_EVENTS',
  AVAILABILITY = 'AVAILABILITY',
  REMINDERS = 'REMINDERS',
  BULK_SCHEDULING = 'BULK_SCHEDULING',

  // Recording
  LOCAL_RECORDING = 'LOCAL_RECORDING',
  CLOUD_RECORDING = 'CLOUD_RECORDING',
  HIDE_STUDENT_VIDEO_FROM_RECORDING = 'HIDE_STUDENT_VIDEO_FROM_RECORDING',
  DOWNLOADABLE_CLOUD_RECORDINGS = 'DOWNLOADABLE_CLOUD_RECORDINGS',
  RECORDING_OF_BREAKOUT_ROOMS = 'RECORDING_OF_BREAKOUT_ROOMS',
  EXPORT_TO_S3_BUCKET = 'EXPORT_TO_S3_BUCKET',
  CLOUD_RECORDING_STORAGE_MONTHS = 'CLOUD_RECORDING_STORAGE_MONTHS',

  // Apps
  IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE = 'IMPORT_FROM_ONEDRIVE_GOOGLE_DRIVE',
  IMPORT_FROM_PENCIL_FILES = 'IMPORT_FROM_PENCIL_FILES',
  POLLS_TIMER_STOPWATCH = 'POLLS_TIMER_STOPWATCH',
  GOOGLE_DOCS_SHEETS_SLIDES = 'GOOGLE_DOCS_SHEETS_SLIDES',
  KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME = 'KAHOOT_NEARPOD_WAYGROUND_QUIZLET_IFRAME',
  COBROWSER = 'COBROWSER',
  COLLABORATIVE_BUILT_IN_APPS = 'COLLABORATIVE_BUILT_IN_APPS',

  // AI and Alerts
  SESSION_TRANSCRIPTS = 'SESSION_TRANSCRIPTS',
  SESSION_SUMMARIES = 'SESSION_SUMMARIES',
  LESSON_GENERATOR = 'LESSON_GENERATOR',
  AUTOGRADER = 'AUTOGRADER',
  CUSTOM_SUMMARY_TEMPLATES = 'CUSTOM_SUMMARY_TEMPLATES',
  POST_SESSION_COACH = 'POST_SESSION_COACH',
  POST_SESSION_SAFEGUARDING_REPORT = 'POST_SESSION_SAFEGUARDING_REPORT',
  POST_SESSION_DISINTERMEDIATION_ALERT = 'POST_SESSION_DISINTERMEDIATION_ALERT',
  POST_SESSION_STUDENT_REPORT = 'POST_SESSION_STUDENT_REPORT',
  REAL_TIME_SAFEGUARDING_ALERTS = 'REAL_TIME_SAFEGUARDING_ALERTS',
  REAL_TIME_COACH = 'REAL_TIME_COACH',
  CLOSED_CAPTIONS = 'CLOSED_CAPTIONS',
  ADDON_REAL_TIME_AUDIO_TRANSLATION = 'ADDON_REAL_TIME_AUDIO_TRANSLATION',

  // Analytics
  SESSIONS_HISTORY_VIEW = 'SESSIONS_HISTORY_VIEW',
  SESSION_FEEDBACK = 'SESSION_FEEDBACK',
  PER_SESSION_ENGAGEMENT_ANALYTICS = 'PER_SESSION_ENGAGEMENT_ANALYTICS',
  ALERT_CENTER = 'ALERT_CENTER',
  TEACHER_STUDENT_DASHBOARDS = 'TEACHER_STUDENT_DASHBOARDS',

  // Integrations
  NATIVE_APPS = 'NATIVE_APPS',
  TMS_LMS_INTEGRATIONS = 'TMS_LMS_INTEGRATIONS',
  API_AND_WEBHOOKS = 'API_AND_WEBHOOKS',
  CHATGPT_CLAUDE_INTEGRATION = 'CHATGPT_CLAUDE_INTEGRATION',

  // Training, Onboarding, and Support
  LOCALIZATION_AND_RTL = 'LOCALIZATION_AND_RTL',
  SUPPORT_24_7 = 'SUPPORT_24_7',
  SELF_SERVE_TRAINING = 'SELF_SERVE_TRAINING',
  DEDICATED_ADMIN_ONBOARDING = 'DEDICATED_ADMIN_ONBOARDING',
  DEDICATED_ACCOUNT_MANAGER = 'DEDICATED_ACCOUNT_MANAGER',

  // Enterprise
  COMPLIANCE = 'COMPLIANCE',
  INSTITUTION_TEMPLATES = 'INSTITUTION_TEMPLATES',
  BRANDING = 'BRANDING',
  MULTI_SITE_SUPPORT = 'MULTI_SITE_SUPPORT',
  VOLUME_BASED_DISCOUNTS = 'VOLUME_BASED_DISCOUNTS',
  BULK_PRE_PURCHASE_HOURS = 'BULK_PRE_PURCHASE_HOURS',
  ADDON_CUSTOM_DEVELOPMENT = 'ADDON_CUSTOM_DEVELOPMENT',
  ADDON_SIERRA_TMS = 'ADDON_SIERRA_TMS',
}

export interface PaymentPlanFeature {
  name: FeatureName;
  value?: string;
  isIncludedInPlan?: boolean;
}

export enum FeatureType {
  PRICING = 'PRICING',
  PER_SESSION_COST = 'PER_SESSION_COST',
  LIVE_TEACHING = 'LIVE_TEACHING',
  CALLING = 'CALLING',
  SCHEDULING = 'SCHEDULING',
  RECORDING = 'RECORDING',
  APPS = 'APPS',
  AI_AND_ALERTS = 'AI_AND_ALERTS',
  ANALYTICS = 'ANALYTICS',
  INTEGRATIONS = 'INTEGRATIONS',
  TRAINING_ONBOARDING_SUPPORT = 'TRAINING_ONBOARDING_SUPPORT',
  ENTERPRISE = 'ENTERPRISE',
}

export interface Feature {
  name: FeatureName;
  value: string;
  type: FeatureType;
}

export interface MappedFeature {
  name: FeatureName;
  value: string;
  type: FeatureType;
  enabled: boolean;
}

/**
 * Per-plan feature mapping. The key is dynamically built from
 * `${billingStrategy}:${billingFrequency}:${planName}` — no hardcoded strategy/frequency/name.
 */
export interface PlanFeatureSet {
  billingStrategy: BillingStrategy;
  billingFrequency: BillingFrequency;
  planName: string;
  features: PaymentPlanFeature[];
  mappedFeaturesByCategory?: Map<FeatureType, MappedFeature[]>;
}

export interface PaymentPlan {
  name: string;
  tag?: string;
  description?: string;
  pricingPattern?: string;
  planOfferingDescription?: string;
  originalPriceRate?: string;
  currentPriceRate?: string;
  pricingPointDiscountLabel?: string;
  keyFeatures: string[];
  planId?: string;
  price?: number;
  /** When set, shown as /hr (etc.) instead of /month or /year. */
  priceUnit?: string;
  action: PlanAction;
  billingFrequency: BillingFrequency;
  billingStrategy: BillingStrategy;
  isTrialPlan: boolean;
  isHiddenFromPortal: boolean;
  isActive: boolean;
  tier?: string;
  billingConfiguration?: BillableFeatureConfig[];
  /** When true, checkout/webhook apply V2 SpaceDuration billing on the institution. */
  enableV2Billing?: boolean;
  /** Copied to institution.paywallConfig.stepPricingConfiguration at apply time. */
  stepPricingConfiguration?: StepPricingConfig;
}

export interface ChargebeeSubscriptionData {
  planId: string;
  subscriptionId: string;
  customerId: string;
  chargebeeEmail: string;
  currency: string;
}

export interface CheckoutSuccessResponse {
  message: string;
  subscriptionData: ChargebeeSubscriptionData;
}
